import memoize from 'transmute/memoize';
import omit from 'transmute/omit';
import Raven from 'raven-js';
import * as ConnectorEdgeTypes from 'automation-ui-action-features/constants/ConnectorEdgeTypes';
import * as SpecialActionIds from 'automation-ui-flow-core/canvas/SpecialActionIds';
import Edge, { EdgeType } from 'automation-ui-directed-graph/Edge';
import DirectedNodes from 'automation-ui-directed-graph/DirectedNodes';
import uniqueIdForObject from 'automation-ui-core/utils/uniqueIdForObject';
import * as CanvasIdentifiers from 'automation-ui-flow-core/canvas/CanvasIdentifiers';
import { getClassicEventAnchor, getFirstActionId } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import { createReverseEdgeMap } from 'automation-ui-action-features/records/recordsUtils';
import { getNodeHeights } from 'automation-ui-canvas/utils/getNodeHeights';
import Metrics from 'automation-ui-canvas/utils/metrics';

/**
 * By traversing the edges in reverse, we detect any node
 * that contains a set of parent edges without a
 * STANDARD connection. This is an illegal representation
 * of a graph since the canvas renderer would effectively
 * hide that node (and all those below it) in these cases.
 *
 * This function will modify one of those GO_TO edges to
 * be a STANDARD edge, and it will remove that edge from
 * the goToPaths.
 */
const cleanActionsWithNoStandardParentEdges = ({
  flow,
  edges,
  goToPaths,
  startingNodeId
}) => {
  const reverseEdgeMap = createReverseEdgeMap(flow.getActions());
  let newGoToPaths = Object.assign({}, goToPaths);
  const newEdges = [...edges];
  Object.keys(reverseEdgeMap).forEach(toNode => {
    const fromNodes = reverseEdgeMap[toNode];
    if (fromNodes.length && toNode !== String(startingNodeId)) {
      const standardFromNode = fromNodes.filter(node => node.edgeType === ConnectorEdgeTypes.STANDARD);
      if (!standardFromNode.length) {
        const firstEdge = fromNodes[0];
        const edgeToModifyIndex = newEdges.findIndex(edge => edge !== undefined && edge.edgeId === firstEdge.edgeId);
        const edgeToModify = newEdges[edgeToModifyIndex];
        newGoToPaths = omit([edgeToModify.toNode], newGoToPaths);
        Metrics.counter('cleanActionsWithNoStandardParentEdges-cleaned').increment();
        Raven.captureMessage('cleanActionsWithNoStandardParentEdges did some cleaning', {
          extra: {
            flow
          }
        });
        newEdges[edgeToModifyIndex] = edgeToModify.set('toNode', parseInt(toNode, 10));
      }
    }
  });
  return {
    goToPaths: newGoToPaths,
    edges: newEdges
  };
};
const getIsLoopReturnEdge = (connector, flow) => {
  const nextActionId = connector && connector.getNextActionId();
  const nextAction = nextActionId && flow.getActionById(nextActionId);
  const isLoopReturnEdge = nextAction && nextAction.isLoopBranch();
  return !!isLoopReturnEdge;
};
export const translateToGraph = memoize((flow, renderGoToEdges = false, nodeWidth, nodeHeight, showEnrollmentCard = true) => {
  let goToActionCounter = 0;
  const goToPaths = {};
  function createEdge(fromActionId, edgeModifier, connector, isLoopEdge, isLoopReturnEdge) {
    const nextNode = connector && connector.getNextActionId();
    const isGoToEdge = connector && connector.isGoToEdge();
    const edgeId = `${fromActionId}-edge-${edgeModifier}`;
    let newEdgeToNode = nextNode;
    if (nextNode && !renderGoToEdges && isGoToEdge) {
      newEdgeToNode = `${edgeId}-${SpecialActionIds.GO_TO}-${goToActionCounter}`;
      goToActionCounter++;
      goToPaths[newEdgeToNode] = {
        toNodeId: nextNode
      };
    }
    const edgeType = isGoToEdge ? EdgeType.GOTO : EdgeType.STANDARD;
    return new Edge({
      edgeId,
      edgeType,
      fromNode: fromActionId,
      toNode: newEdgeToNode,
      showAddControl: fromActionId !== SpecialActionIds.NEW_ACTION,
      isLoopEdge,
      isLoopReturnEdge
    });
  }
  const startingNodeId = getFirstActionId(flow);
  const edges = [];
  const nodeIdList = [];
  const isClassicAnchoredFlow = getClassicEventAnchor(flow);
  if (isClassicAnchoredFlow) {
    edges.push(new Edge({
      edgeId: CanvasIdentifiers.FIRST_NODE_EDGE,
      fromNode: CanvasIdentifiers.FIRST_NODE,
      toNode: CanvasIdentifiers.VIRTUAL_ANCHORED_DELAY_NODE,
      showAddControl: false
    }));
    nodeIdList.push(CanvasIdentifiers.FIRST_NODE);
    edges.push(new Edge({
      edgeId: CanvasIdentifiers.VIRTUAL_ANCHORED_DELAY_NODE_EDGE,
      fromNode: CanvasIdentifiers.VIRTUAL_ANCHORED_DELAY_NODE,
      toNode: startingNodeId,
      showAddControl: false
    }));
    nodeIdList.push(CanvasIdentifiers.VIRTUAL_ANCHORED_DELAY_NODE);
  } else {
    edges.push(new Edge({
      edgeId: CanvasIdentifiers.FIRST_NODE_EDGE,
      fromNode: CanvasIdentifiers.FIRST_NODE,
      toNode: startingNodeId,
      showAddControl: true
    }));
    nodeIdList.push(CanvasIdentifiers.FIRST_NODE);
  }
  flow.getActions().valueSeq().forEach(action => {
    let edgeModifier = 1;
    const actionId = action.getActionId();
    nodeIdList.push(actionId);
    const actionConnection = action.getConnection();
    if (actionConnection) {
      if (actionConnection.isBranchConnection()) {
        const isLoopEdge = action.isLoopBranch();
        const branchConnection = actionConnection;
        const defaultConnector = branchConnection.getDefaultConnector();
        // saving the default branch with the lowest edge modifier for after
        // the other branches are added so it appears on the right
        const defaultBranchEdge = createEdge(actionId, edgeModifier, defaultConnector, false, getIsLoopReturnEdge(defaultConnector, flow));
        edgeModifier += 1;
        branchConnection.getBranches().forEach(branch => {
          const branchConnector = branch.getConnector();
          const isLoopReturnEdge = getIsLoopReturnEdge(branchConnector, flow);
          edges.push(createEdge(actionId, edgeModifier, branchConnector, isLoopEdge, isLoopReturnEdge));
          edgeModifier += 1;
        });

        // AB Test Branches never have a default branch
        if (!action.isABTestBranch()) {
          edges.push(defaultBranchEdge);
        }
      } else {
        actionConnection.getConnectors().forEach(connector => {
          const isLoopReturnEdge = getIsLoopReturnEdge(connector, flow);
          edges.push(createEdge(actionId, edgeModifier, connector, undefined, isLoopReturnEdge));
          edgeModifier += 1;
        });
      }
    } else {
      edges.push(createEdge(actionId, edgeModifier, null));
      edgeModifier += 1;
    }
  });
  const cleanEdges = cleanActionsWithNoStandardParentEdges({
    flow,
    edges,
    goToPaths,
    startingNodeId
  });
  return new DirectedNodes({
    startingNodeId: 'enrollment',
    nodeIds: nodeIdList,
    defaultNodeHeights: getNodeHeights({
      showEnrollmentCard
    }),
    edges: cleanEdges.edges,
    goToPaths: cleanEdges.goToPaths,
    nodeWidth,
    nodeHeight
  });
}, (flow, renderGoToEdges, nodeWidth, nodeHeight, showEnrollmentCard = true) => `${uniqueIdForObject(flow)}-${renderGoToEdges}-${nodeWidth}-${nodeHeight}-${showEnrollmentCard}`);